import { createContext, useMemo, ReactNode, useCallback, useState } from 'react';
import { IMarker } from 'src/interfaces/Marker';
import useMarker from 'src/hooks/useMarker';
import { MARKER_ACTION, MARKER_STATUS } from 'src/interfaces/enums';
import { MAX_NUMBER_OF_ATTEMPTS_TO_SAVE_NOTE } from '../constants';

interface INotesContextProps {
  children: ReactNode;
}

export interface IMarkerPending extends IMarker {
  action?: MARKER_ACTION;
  status?: MARKER_STATUS;
  numberOfAttempts?: number;
}

type MarkerPendingProps = {
  [key in keyof IMarker['id']]-?: IMarkerPending;
};

interface INotesContextData {
  notesPending: IMarkerPending[];
  handleAddNote: (note: IMarker) => Promise<void>;
  handleUpdateNote: (note: IMarker) => Promise<void>;
  handleRemoveNote: (note: IMarker) => Promise<void>;
  handleRetry: (pendingNote: IMarkerPending, onMaxAttemptReached?: () => void) => Promise<void>;
  getPendingNoteById: (id: IMarker['id']) => IMarkerPending | undefined;
}

const canAttemptAgain = (numberOfAttempts = 0) => {
  return numberOfAttempts < MAX_NUMBER_OF_ATTEMPTS_TO_SAVE_NOTE;
};

export const NotesContext = createContext<INotesContextData>({} as INotesContextData);

const NotesContextProvider = ({ children }: INotesContextProps) => {
  const { saveNewMarker, saveUpdatedMarker, deleteMarker, insertPendingMarker, removePendingMarker } = useMarker();

  const [notesPending, setNotesPending] = useState<MarkerPendingProps>({} as MarkerPendingProps);

  const updatePendingNote = useCallback((note: IMarkerPending) => {
    setNotesPending((currentState) => ({ ...currentState, [`${note.id}`]: note }));
  }, []);

  const removePendingNote = useCallback(
    (pendingNote: IMarkerPending) => {
      setNotesPending((currentState) => {
        const newNotesPending = { ...currentState };
        delete newNotesPending[`${pendingNote.id}`];
        return newNotesPending;
      });
      if (pendingNote.action === MARKER_ACTION.CREATE) {
        removePendingMarker(pendingNote.id);
      }
    },
    [removePendingMarker],
  );

  const turnNoteIntoPending = useCallback(
    (note: IMarkerPending) => {
      updatePendingNote({ ...note, status: MARKER_STATUS.PENDING });
    },
    [updatePendingNote],
  );

  const getPendingNoteById = useCallback(
    (id: IMarker['id']) => {
      return notesPending[`${id}`];
    },
    [notesPending],
  );

  const handleAddNote = useCallback(
    async (note: IMarker) => {
      const pendingNote: IMarkerPending = {
        ...note,
        id: note.id ?? insertPendingMarker(note),
        action: MARKER_ACTION.CREATE,
      };
      turnNoteIntoPending(pendingNote);
      try {
        await saveNewMarker(note);
        removePendingNote(pendingNote);
      } catch (error) {
        pendingNote.status = MARKER_STATUS.ERROR;
        pendingNote.numberOfAttempts = pendingNote.numberOfAttempts ? pendingNote.numberOfAttempts + 1 : 1;
        updatePendingNote(pendingNote);
      }
    },
    [turnNoteIntoPending, insertPendingMarker, removePendingNote, saveNewMarker, updatePendingNote],
  );

  const handleUpdateNote = useCallback(
    async (note: IMarker) => {
      const pendingNote = note as IMarkerPending;
      pendingNote.action = MARKER_ACTION.EDIT;
      turnNoteIntoPending(pendingNote);
      try {
        await saveUpdatedMarker(note);
        removePendingNote(pendingNote);
      } catch (error) {
        pendingNote.status = MARKER_STATUS.ERROR;
        pendingNote.numberOfAttempts = pendingNote.numberOfAttempts ? pendingNote.numberOfAttempts + 1 : 1;
        updatePendingNote(pendingNote);
      }
    },
    [turnNoteIntoPending, removePendingNote, saveUpdatedMarker, updatePendingNote],
  );

  const handleRemoveNote = useCallback(
    async (note: IMarker) => {
      const pendingNote = note as IMarkerPending;
      pendingNote.action = MARKER_ACTION.DELETE;
      turnNoteIntoPending(pendingNote);
      try {
        await deleteMarker(note.id);
        removePendingNote(pendingNote);
      } catch (error) {
        const pendingNote = note as IMarkerPending;
        pendingNote.status = MARKER_STATUS.ERROR;
        pendingNote.numberOfAttempts = pendingNote.numberOfAttempts ? pendingNote.numberOfAttempts + 1 : 1;
        updatePendingNote(pendingNote);
      }
    },
    [turnNoteIntoPending, deleteMarker, removePendingNote, updatePendingNote],
  );

  const handleRetry = useCallback(
    async (pendingNote: IMarkerPending, onMaxAttemptReached) => {
      const canAttemptActionAgain = canAttemptAgain(pendingNote.numberOfAttempts);

      if (!canAttemptActionAgain) {
        removePendingNote(pendingNote);
        onMaxAttemptReached && onMaxAttemptReached();
        return;
      }

      if (pendingNote.action === MARKER_ACTION.DELETE) {
        return handleRemoveNote(pendingNote);
      }

      if (pendingNote.action === MARKER_ACTION.CREATE) {
        return handleAddNote(pendingNote);
      }

      return handleUpdateNote(pendingNote);
    },
    [handleAddNote, handleRemoveNote, handleUpdateNote, removePendingNote],
  );

  const valueProvider = useMemo(() => {
    return {
      notesPending: Object.values(notesPending) as IMarkerPending[],
      handleAddNote,
      handleUpdateNote,
      handleRemoveNote,
      handleRetry,
      getPendingNoteById,
    };
  }, [notesPending, handleAddNote, handleUpdateNote, handleRemoveNote, handleRetry, getPendingNoteById]);

  return <NotesContext.Provider value={valueProvider}>{children}</NotesContext.Provider>;
};

type WithNotesProviderProps<T> = T & {
  children?: React.ReactNode;
};

export default NotesContextProvider;

export const withNotesContextProvider = <T,>(Component: React.ComponentType<T>) => {
  return function (props: WithNotesProviderProps<T>) {
    return (
      <NotesContextProvider>
        <Component {...props}>{props.children}</Component>
      </NotesContextProvider>
    );
  };
};
